import React, {useContext, useEffect, useState, useCallback} from "react";
import StyleContext from "../../contexts/StyleContext";
import {useAuth} from "../../contexts/AuthContext";
import {
  adminListMessages,
  adminMarkMessageRead,
  adminDeleteMessage
} from "../../services/api";
import Loader from "../../components/loader/Loader";
import "./AdminInbox.scss";

const fmt = ts => {
  try { return new Date(ts.replace(" ", "T") + "Z").toLocaleString(); }
  catch (_) { return ts; }
};

const AdminInbox = () => {
  const {isDark} = useContext(StyleContext);
  const {token} = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [busyId, setBusyId]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminListMessages(token);
      setMessages(res.messages || []);
      if (res.messages?.length && selectedId == null) {
        setSelectedId(res.messages[0].id);
      }
    } catch (e) {
      setError(e.message || "Could not load inbox");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const selected = messages.find(m => m.id === selectedId) || null;

  const openMessage = async msg => {
    setSelectedId(msg.id);
    if (!msg.is_read) {
      setBusyId(msg.id);
      try {
        await adminMarkMessageRead(msg.id, 1, token);
        setMessages(list => list.map(m => m.id === msg.id ? {...m, is_read: 1} : m));
      } catch (_) { /* visual only — keep optimistic */ }
      finally { setBusyId(null); }
    }
  };

  const toggleRead = async msg => {
    setBusyId(msg.id);
    try {
      const next = msg.is_read ? 0 : 1;
      await adminMarkMessageRead(msg.id, next, token);
      setMessages(list => list.map(m => m.id === msg.id ? {...m, is_read: next} : m));
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  const remove = async msg => {
    if (!window.confirm(`Delete message from "${msg.name}"? This cannot be undone.`)) return;
    setBusyId(msg.id);
    try {
      await adminDeleteMessage(msg.id, token);
      setMessages(list => list.filter(m => m.id !== msg.id));
      if (selectedId === msg.id) setSelectedId(null);
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  if (loading) return <Loader inline label="Loading inbox" />;

  return (
    <div className={`rd-inbox ${isDark ? "dark" : ""}`}>
      {error && <p className="modal__error">{error}</p>}

      {messages.length === 0 ? (
        <div className="rd-inbox__empty">
          <h3>Inbox zero ✨</h3>
          <p>No contact form submissions yet.</p>
        </div>
      ) : (
        <div className="rd-inbox__split">
          <ul className="rd-inbox__list">
            {messages.map(m => (
              <li
                key={m.id}
                className={
                  "rd-inbox__item" +
                  (m.id === selectedId ? " is-selected" : "") +
                  (m.is_read ? "" : " is-unread")
                }
                onClick={() => openMessage(m)}
              >
                <div className="rd-inbox__from">
                  {!m.is_read && <span className="rd-inbox__dot" aria-label="unread" />}
                  {m.name}
                </div>
                <div className="rd-inbox__subj">{m.subject}</div>
                <div className="rd-inbox__when">{fmt(m.created_at)}</div>
              </li>
            ))}
          </ul>

          <div className="rd-inbox__detail">
            {selected ? (
              <>
                <div className="rd-inbox__detail-head">
                  <div>
                    <h3>{selected.subject}</h3>
                    <p className="rd-inbox__meta">
                      <strong>{selected.name}</strong>{" · "}
                      <a href={`mailto:${selected.email}`}>{selected.email}</a>
                      {" · "}<span>{fmt(selected.created_at)}</span>
                    </p>
                    {!selected.email_sent && (
                      <p className="rd-inbox__warn">
                        ⚠ Email notification didn't send for this one — message saved to DB.
                      </p>
                    )}
                  </div>
                  <div className="rd-inbox__actions">
                    <button
                      onClick={() => toggleRead(selected)}
                      disabled={busyId === selected.id}
                    >
                      {selected.is_read ? "Mark unread" : "Mark read"}
                    </button>
                    <a
                      className="rd-inbox__reply"
                      href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                    >
                      Reply
                    </a>
                    <button
                      className="danger"
                      onClick={() => remove(selected)}
                      disabled={busyId === selected.id}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <pre className="rd-inbox__body">{selected.message}</pre>
              </>
            ) : (
              <p className="rd-inbox__pick">Select a message to read it.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
